// Where "/" goes, decided by what the signed-in person is authorised to do.
//
// WHY THIS IS NOT JUST A REDIRECT. `/` used to send everybody to `/op`,
// unconditionally, with a good reason written beside it: the line is by far the
// larger population, and an operator who has to navigate to their own screen is
// an operator who sometimes does not. That reasoning still holds — for
// operators. It was wrong for everybody else. QA managers, shift leaders and
// anyone doing the set-up work landed on a line screen they had no use for and
// then had to know that `/office` existed, because nothing links to it.
//
// ⚠ THE FIX IS NOT TO FLIP THE DEFAULT. Sending everybody to the office would
// make the same mistake pointing the other way, and hurt the larger group.
// Authority decides: hold an office grant and you land in the office; otherwise
// you land on the line.
//
// THE REDIRECT IS NOT A PERMISSION. It is a convenience, and it must never be
// read as one. Every control in the office is gated on its own grant, and the
// server checks again regardless — somebody who reaches `/office` by typing it
// sees the shell and can do nothing they are not authorised to do. That is why
// an unknown answer below is allowed to fall through to the line rather than
// being treated as a security decision.
import { Navigate } from 'react-router-dom';
import { currentUpn } from '../config/auth';
import { OFFICE_ACTIONS, useMyGrants } from '../shells/OfficeShell';

/**
 * The grants that mean "this person's work is at a desk".
 *
 * CAPTURE and VERIFY are deliberately NOT here. Both are done at the line — a
 * shift leader who verifies holds VERIFY and still belongs on the operator
 * shell, which is where the verify screen lives. ARTWORK_SIGNOFF is not here
 * either: artwork is signed off in chat, and there is no artwork screen to send
 * anybody to.
 */
// One list, the office shell's: the Office tab on the tablet and this landing agree (audit, 28 Oct 2026).

export function Landing() {
  const grants = useMyGrants();
  const upn = currentUpn();

  // ⚠ NO UPN, NO WAITING. useMyGrants asks for nothing when there is no signed-in
  // account, so `known` stays false and `error` stays null FOREVER — and the
  // branch below would sit on "Signing you in…" with no way out. Reachable in
  // local auth-bypass, and in production by any path where the account is
  // present but the UPN claim is not. A permanent spinner is the worst of the
  // three outcomes; no account means no authority, which means the line.
  if (!upn) return <Navigate to="/op" replace />;

  // `known` is false while the register is still being read AND if reading it
  // failed. Both are "we do not know yet", and neither should flash the wrong
  // screen — a redirect that happens twice is worse than one that waits a beat.
  if (!grants.known && grants.error === null) {
    return (
      <div className="flex h-full items-center justify-center p-(--s4)">
        <p className="m-0 text-(length:--fs-md) text-(--muted)">Signing you in…</p>
      </div>
    );
  }

  // The register could not be read. Fall through to the line: it is the safe
  // default for the larger population, and the office is one URL away for
  // anybody who needs it. Failing to the office instead would strand an
  // operator on a screen full of controls they cannot use.
  const office = grants.known && OFFICE_ACTIONS.some((a) => grants.holds(a));
  // RECEIVE without CAPTURE is the warehouse at the dock (migration 017): their one
  // screen is the incoming inspection, on the operator shell.
  const dock = grants.known && !office && grants.holds('RECEIVE') && !grants.holds('CAPTURE');
  return <Navigate to={office ? '/office' : dock ? '/op/receive' : '/op'} replace />;
}

export default Landing;
